import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useIntl } from 'react-intl';

import ResponsiveImage from '../../../components/ResponsiveImage/ResponsiveImage';
import AlgoliaKeywordSearch from '../../../components/AlgoliaKeywordSearch/AlgoliaKeywordSearch';
import AlgoliaLocationSearch from '../../../components/AlgoliaLocationSearch/AlgoliaLocationSearch';
import { useLocationBasedPlaceholder } from '../../../hooks/useLocationBasedPlaceholder';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { useConfiguration } from '../../../context/configurationContext';
import { createResourceLocatorString } from '../../../util/routes';
import { parse } from '../../../util/urlHelpers';
import { getCountryName } from '../../../util/data';

import css from './HeroSection.module.css';

const DEFAULT_LOCATION_MESSAGE_ID = 'HeroSection.defaultLocation';
const FALLBACK_HEADLINE_MESSAGE_ID = 'HeroSection.fallbackHeadline';
const MOBILE_BREAKPOINT = 1024;

const getIsDesktop = () => {
    if (typeof window === 'undefined') {
        return true;
    }
    return window.innerWidth > MOBILE_BREAKPOINT;
};

const deriveLocationLabel = (locationInfo, fallback) => {
    const { city, state, country } = locationInfo || {};

    if (city) {
        return city;
    }

    return fallback;
};

const pickImageVariant = image => {
    if (!image) {
        return null;
    }

    const variants =
        image?.attributes?.variants ||
        image?.image?.attributes?.variants ||
        image?.backgroundImage?.attributes?.variants;

    if (!variants) {
        return null;
    }

    const preferredOrder = [
        'landscape1200',
        'landscape800',
        'landscape1600',
        'landscape2400',
        'original1200',
        'original800',
        'original1600',
        'original2400',
        'square1200',
    ];
    for (const key of preferredOrder) {
        if (variants[key]?.url) {
            return variants[key].url;
        }
    }

    const firstVariant = Object.values(variants).find(variant => variant?.url);
    return firstVariant?.url || null;
};

const parseHeadlineTemplate = (template, fallbackTemplate) => {
    const rawTemplate = (template || '').trim() || (fallbackTemplate || '');
    const pattern = /\{\{(.*?)\}\}/g;

    const segments = [];
    let lastIndex = 0;
    let match = pattern.exec(rawTemplate);
    while (match) {
        const [token, value] = match;
        const start = match.index;

        if (start > lastIndex) {
            segments.push({ type: 'text', value: rawTemplate.slice(lastIndex, start) });
        }

        const tokenName = value.trim().toLowerCase();
        if (tokenName === 'location') {
            segments.push({ type: 'location' });
        } else {
            segments.push({ type: 'highlight', value: value.trim() });
        }

        lastIndex = start + token.length;
        match = pattern.exec(rawTemplate);
    }

    if (lastIndex < rawTemplate.length) {
        segments.push({ type: 'text', value: rawTemplate.slice(lastIndex) });
    }

    return segments;
};

const HeroSection = props => {
    const {
        sectionId = 'hero',
        title,
        appearance,
        blocks,
    } = props;

    const history = useHistory();
    const routeConfiguration = useRouteConfiguration();
    const config = useConfiguration();
    const intl = useIntl();
    const { currentUser, currentUserLocationInfo, currentUserCurrency } = useSelector(state => state.user);
    const { exchangeRates } = useSelector(state => state.exchangeRate);

    const queryParams = useMemo(
        () =>
            parse(history?.location?.search, {
                latlng: ['origin'],
                latlngBounds: ['bounds'],
            }) || {},
        [history?.location?.search]
    );

    const [keywordValues, setKeywordValues] = useState(null);
    const [isSticky, setIsSticky] = useState(false);
    const defaultLocationLabel = useMemo(
        () => intl.formatMessage({ id: DEFAULT_LOCATION_MESSAGE_ID }),
        [intl]
    );
    const fallbackHeadlineTemplate = useMemo(
        () => intl.formatMessage({ id: FALLBACK_HEADLINE_MESSAGE_ID }),
        [intl]
    );

    const [locationLabel, setLocationLabel] = useState(defaultLocationLabel);
    const searchRef = useRef(null);

    const keywordPlaceholderKey = getIsDesktop()
        ? 'AlgoliaKeywordSearch.placeholder'
        : 'AlgoliaKeywordSearch.placeholderMobile';
    const keywordPlaceholder = useLocationBasedPlaceholder(keywordPlaceholderKey);
    const locationPlaceholder = useLocationBasedPlaceholder('AlgoliaLocationSearch.placeholder');

    useEffect(() => {
        setLocationLabel(deriveLocationLabel(currentUserLocationInfo, defaultLocationLabel));
    }, [queryParams, currentUserLocationInfo, defaultLocationLabel]);

    const handleKeywordChange = value => {
        setKeywordValues(prev => ({
            ...prev,
            keyword: value,
        }));
    };

    const handleLocationChange = value => {
        const { address, bounds, origin, city, country } = value || {};

        if (address && bounds) {
            const searchParams = {
                address,
                bounds,
                origin,
                city,
                country,
            };

            history.push(
                createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams)
            );
        }
    };

    const handleSearchSubmit = event => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }

        const { keyword } = keywordValues || {};
        const { address, bounds: latlngBounds, origin, city, country } = currentUserLocationInfo || {};
        const countryName = getCountryName(country);
        const hasResolvedSearchArea = !!latlngBounds;

        const searchParams = {
            ...(keyword?.trim() ? { keyword } : {}),
            ...(hasResolvedSearchArea && address?.trim() ? { address } : {}),
            ...(hasResolvedSearchArea ? { bounds: latlngBounds } : {}),
            ...(hasResolvedSearchArea && origin ? { origin } : {}),
            ...(hasResolvedSearchArea && city ? { city } : {}),
            ...(hasResolvedSearchArea && countryName ? { country: countryName } : {}),
        };

        const searchPath = createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams);

        history.push(searchPath,{ fromLandingPage: true });
    };

    useEffect(() => {
        if (typeof window === 'undefined') {
            return () => { };
        }

        const handleScroll = () => {
            if (window.innerWidth > MOBILE_BREAKPOINT) {
                if (isSticky) {
                    setIsSticky(false);
                }
                return;
            }

            if (!searchRef.current) {
                return;
            }

            const rect = searchRef.current.getBoundingClientRect();
            const threshold = 50;

            if (rect.top <= threshold && !isSticky) {
                setIsSticky(true);
            } else if (rect.top > threshold && isSticky) {
                if (rect.top > threshold + rect.height / 2) {
                    setIsSticky(false);
                }
            }
        };

        const handleResize = () => {
            if (window.innerWidth > MOBILE_BREAKPOINT && isSticky) {
                setIsSticky(false);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [isSticky]);

    const patternImageUrl = pickImageVariant(appearance?.backgroundImage);
    const backgroundColor = appearance?.backgroundColor || '#fdeed7';
    const headlineSegments = useMemo(
        () => parseHeadlineTemplate(title?.content, fallbackHeadlineTemplate),
        [title?.content, fallbackHeadlineTemplate]
    );

    let highlightCount = 0;

    const renderedHeadline = headlineSegments.map((segment, index) => {
        if (segment.type === 'text') {
            return (
                <span key={`text-${index}`} className={css.headlineText}>
                    {segment.value}
                </span>
            );
        }

        if (segment.type === 'location') {
            return (
                <span key={`location-${index}`} className={css.locationLabel}>
                    {locationLabel}
                </span>
            );
        }

        highlightCount += 1;
        const className = highlightCount === 1 ? css.highlightPrimary : css.highlightSecondary;

        return (
            <>
            {highlightCount === 1 ? null : <br/> }
            <span key={`highlight-${index}`} className={className}>
                {segment.value}
            </span>
            </>
        );
    });

    const primaryCtaContent = blocks?.[0]?.callToAction?.content;
    const hasCta = false;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    // Construct custom Cloudinary URLs with exact transformations
    const mobileImageUrl = `https://res.cloudinary.com/dja5jmxiz/image/upload/v1762925994/Mobile_Hero_BG_fvgjzs.png`;
    const desktopImageUrl = `https://res.cloudinary.com/dja5jmxiz/image/upload/v1762925994/Desktop_Hero_BG_b1okcp.png`;
    
    const bannerImageUrl = isMobile ? mobileImageUrl : desktopImageUrl;

    const desktopPreloadUrl = `https://res.cloudinary.com/dja5jmxiz/image/upload/w_1440,h_550,f_auto,q_auto:best,dpr_auto//v1762925994/Desktop_Hero_BG_b1okcp.png`;
    const mobilePreloadUrl = `https://res.cloudinary.com/dja5jmxiz/image/upload/w_390,h_625,f_auto,q_auto:best,dpr_auto//v1762925994/Mobile_Hero_BG_fvgjzs.png`;

    return (
        <>
        <Helmet>
          <link rel="preload" as="image" href={desktopPreloadUrl} fetchpriority="high" media="(min-width: 768px)" />
          <link rel="preload" as="image" href={mobilePreloadUrl} fetchpriority="high" media="(max-width: 767px)" />
        </Helmet>
        <section id={sectionId} className={css.root} aria-label="Marketplace hero">
       <div className={css.patternLayer}>
       <ResponsiveImage
                renderCloudinaryImage={{
                    secure_url: bannerImageUrl,
                    alt: 'Hero Section Background',
                    variantsToRender: isMobile 
                      ? ['hero-section-mobile']
                      : ['hero-section-desktop'],
                    defaultVariant: isMobile ? 'banner-600' : 'banner-1800',
                    cloudinaryOptions: {
                        format: 'auto',
                        quality: 'auto:best',
                        crop: 'fill',
                        gravity: 'center',
                        width: isMobile ? 390 : 1920,
                        height: isMobile ? 625 : 550,
                      },
               
                }}
                
            />
       </div>
            <div className={css.overlay} />

            <div className={css.container}>
                <div className={css.headlineShell}>
                    <div className={css.stripes}>
                        <span className={classNames(css.stripe, css.stripeTop)} />
                        <span className={classNames(css.stripe, css.stripeUpper)} />
                        <span className={classNames(css.stripe, css.stripeLower)} />
                        <span className={classNames(css.stripe, css.stripeBottom)} />
                    </div>
                    <div className={css.headlineBadge}>
                        <h1 className={css.headline}>{renderedHeadline}</h1>
                    </div>
                </div>

                <div className={css.searchShell} >
                    <form className={classNames(css.searchForm,)} onSubmit={handleSearchSubmit} noValidate>
                        <AlgoliaLocationSearch
                            className={css.locationInput}
                            defaultLocation={currentUserLocationInfo}
                            onRefineLocation={handleLocationChange}
                            initialValues={{
                                bounds: queryParams.bounds,
                                address: queryParams.address,
                                country: queryParams.country,
                            }}
                            isLandingPage
                            name="location"
                            searchInputText={locationPlaceholder}
                        />
                        <div className={classNames(css.keywordSearchContainer, { [css.isSticky]: isSticky })}>
                            <AlgoliaKeywordSearch
                                onSearchChange={handleKeywordChange}
                                clearKeywordsClass={css.clearKeywords}
                                micClass={css.micIcon}
                                initialValue={{ keyword: queryParams.keyword }}
                                history={history}
                                currentUserCurrency={currentUserCurrency}
                                currentUser={currentUser}
                                exchangeRates={exchangeRates}
                                routes={routeConfiguration}
                                config={config}
                                intl={intl}
                                onFocus={handleSearchSubmit}
                                name="keyword"
                                searchInputText={keywordPlaceholder}
                                blockAutoScroll
                                isLandingPage
                            />
                            <button type="submit" className={css.submitButton} aria-label="Search">
                                <span className={css.submitIcon} />
                            </button>
                        </div>
                    </form>
                </div>

                {hasCta ? <p className={css.supportingCta}>{primaryCtaContent}</p> : null}
            </div>
                <div ref={searchRef} />
        </section>
        </>
    );
};

export default HeroSection;
